import { createFileRoute } from '@tanstack/react-router'
import {
  LogisticsLayout,
  useOpenDetailTab,
  useOpenLogisticsList,
} from '~/components/routes/logistics/logistics-layout'
import { SimulationDetailMap } from '~/components/routes/logistics/simulation-detail-map'
import { LogisticsRouteGuard } from '~/components/routes/logistics/logistics-route-guard'
import { SimulationDetailPanel } from '~/components/routes/logistics/simulation-detail-panel'
import { useSimulationDetailQuery } from '~/components/routes/logistics/use-simulation-detail-query'
import {
  DETAIL_PANEL_WIDTH_PX,
  LIST_PANEL_LEFT_PX,
} from '~/components/routes/logistics/simulator-layout'
import { withRoutePermissions } from '~/utils/rbac'
import {
  type LogisticsSearch,
  type SimulationDetailTab,
  simulationDetailSearch,
} from '../-search-schema'
import { LOGISTICS_SIMULATION_READ } from '~/components/routes/logistics/tabs'

export const Route = createFileRoute('/_authenticated/logistics/simulations/$simulationId')({
  staticData: {
    permission: LOGISTICS_SIMULATION_READ,
  },
  component: SimulationDetailRoute,
  // Mêmes paramètres que la liste : ses filtres suivent l'utilisateur dans le détail et au retour.
  // L'onglet du panneau en plus, que retrouve le retour d'une fiche chauffeur ou camion (LU-181).
  validateSearch: simulationDetailSearch,
})
withRoutePermissions(Route)

/** Mêmes gardes que la liste (`logistics/index.tsx`) : flag `logistique` et droit v2. */
function SimulationDetailRoute() {
  const { simulationId } = Route.useParams()
  // L'onglet du panneau reste propre au détail : la liste ne le reçoit pas.
  const { detailTab = 'contraintes', ...search } = Route.useSearch()
  // La barre du détail mène à la liste : un onglet ou un filtre choisi ici s'y applique.
  const openList = useOpenLogisticsList(search)
  const openDetailTab = useOpenDetailTab<SimulationDetailTab>('contraintes')

  return (
    <LogisticsRouteGuard permission={LOGISTICS_SIMULATION_READ}>
      <SimulationDetail
        id={Number(simulationId)}
        search={search}
        onSearchChange={openList}
        detailTab={detailTab}
        onDetailTabChange={openDetailTab}
      />
    </LogisticsRouteGuard>
  )
}

interface SimulationDetailProps {
  id: number
  search: LogisticsSearch
  onSearchChange: (patch: Partial<LogisticsSearch>) => void
  detailTab: SimulationDetailTab
  onDetailTabChange: (tab: SimulationDetailTab) => void
}

function SimulationDetail({
  id,
  search,
  onSearchChange,
  detailTab,
  onDetailTabChange,
}: SimulationDetailProps) {
  const { detail, runFollowUpError } = useSimulationDetailQuery(id)

  return (
    <LogisticsLayout
      search={search}
      onSearchChange={onSearchChange}
      containerClassName="relative overflow-hidden p-0"
    >
      <SimulationDetailMap detail={detail.data} search={search} />

      <div
        className="absolute inset-y-4 z-10"
        style={{ left: LIST_PANEL_LEFT_PX, width: DETAIL_PANEL_WIDTH_PX }}
      >
        <SimulationDetailPanel
          simulationId={id}
          detail={detail.data}
          isLoading={detail.isLoading}
          error={detail.error}
          runFollowUpError={runFollowUpError}
          tab={detailTab}
          onTabChange={onDetailTabChange}
        />
      </div>
    </LogisticsLayout>
  )
}
